const CART_RESERVATION_STORAGE_KEY = 'gnoce-cart-reservation-expiry';

class CartReservationTimer extends HTMLElement {
  static clearStorage(storageKey = CART_RESERVATION_STORAGE_KEY) {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {}
  }

  connectedCallback() {
    if (this.timerId) return;

    const duration = Number(this.dataset.duration || 900);
    const storageKey = this.dataset.storageKey || CART_RESERVATION_STORAGE_KEY;
    this.duration = duration;
    this.storageKey = storageKey;
    let expiry = 0;

    try {
      expiry = Number(sessionStorage.getItem(storageKey));
    } catch (error) {
      expiry = 0;
    }

    if (!expiry || expiry <= Date.now()) {
      expiry = Date.now() + duration * 1000;
      try {
        sessionStorage.setItem(storageKey, String(expiry));
      } catch (error) {
        expiry = Date.now() + duration * 1000;
      }
    }

    this.expiry = expiry;
    this.restartPending = false;
    this.update();
    this.timerId = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    if (this.timerId) {
      window.clearInterval(this.timerId);
      this.timerId = undefined;
    }
  }

  update() {
    let remaining = Math.max(0, Math.ceil((this.expiry - Date.now()) / 1000));

    if (remaining === 0) {
      if (this.restartPending) {
        this.expiry = Date.now() + this.duration * 1000;
        remaining = this.duration;
        this.restartPending = false;

        try {
          sessionStorage.setItem(this.storageKey, String(this.expiry));
        } catch (error) {}
      } else {
        this.restartPending = true;
      }
    } else {
      this.restartPending = false;
    }

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    this.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

customElements.define('cart-reservation-timer', CartReservationTimer);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    if (this.classList.contains('is-empty')) {
      CartReservationTimer.clearStorage();
    }

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.emptyStateObserver = new MutationObserver(() => {
      if (this.classList.contains('is-empty')) {
        CartReservationTimer.clearStorage();
      }
    });
    this.emptyStateObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

    if (this.classList.contains('is-empty')) {
      CartReservationTimer.clearStorage();
    }
  }

  disconnectedCallback() {
    this.emptyStateObserver?.disconnect();
    this.emptyStateObserver = undefined;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {

}

customElements.define('cart-drawer-items', CartDrawerItems);
